import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Box,
  Grid,
  Paper,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Backdrop
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Star,
  LocationOn,
  Email,
  Phone,
  Close,
  Check,
  Clear,
  Message,
  Notifications,
  Person,
  ChevronRight,
  FilterList,
  Search,
  ExpandMore,
  Business,
  AttachMoney,
  Schedule,
  VideoCall
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const TalentInterviewDashboard = ({collapsed}) => {
  const [activeTab, setActiveTab] = useState(0); // 0: pending, 1: accepted, 2: declined, 3: all
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [responseData, setResponseData] = useState({
    availableDates: [],
    availableTimeSlots: [],
    message: '',
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [allRequests, setAllRequests] = useState([]); // Store all requests for counting
  // API-related state
  const [interviewRequests, setInterviewRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [statistics, setStatistics] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    acceptedRequests: 0,
    declinedRequests: 0,
    completedRequests: 0,
    cancelledRequests: 0,
    responseRate: 0
  });

  // User data from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const candidateId = user.data?.[0]?.c_id || user.data?.[0]?.candidate_id;
  const clientCode = user.data?.[0]?.talent_code;

  // Styled components
  const StyledCard = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    transition: theme.transitions.create(['box-shadow', 'transform']),
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
    },
  }));

  const UrgencyChip = styled(Chip)(({ urgency, theme }) => {
    const colors = {
      high: { bg: theme.palette.error.light, color: theme.palette.error.dark },
      medium: { bg: theme.palette.warning.light, color: theme.palette.warning.dark },
      low: { bg: theme.palette.success.light, color: theme.palette.success.dark }
    };
    return {
      backgroundColor: colors[urgency]?.bg || theme.palette.grey[200],
      color: colors[urgency]?.color || theme.palette.grey[700],
    };
  });

  const StatusChip = styled(Chip)(({ status, theme }) => {
    const colors = {
      pending: { bg: theme.palette.info.light, color: theme.palette.info.dark },
      accepted: { bg: theme.palette.success.light, color: theme.palette.success.dark },
      declined: { bg: theme.palette.error.light, color: theme.palette.error.dark }
    };
    return {
      backgroundColor: colors[status]?.bg || theme.palette.grey[200],
      color: colors[status]?.color || theme.palette.grey[700],
    };
  });

  const timeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM"
  ];

  const tabConfig = ['pending', 'accepted', 'declined', 'all'];

  // API Functions
 const loadInterviewRequests = async (status = 'all') => {
  setLoading(true);
  try {
    const requestData = {
      candidate_id: candidateId,
      client_code: clientCode,
      page: 1,
      limit: 100
    };

    if (status !== 'all') {
      requestData.status = status;
    }

    console.log('Loading interview requests with data:', requestData);

    const response = await axios.post(
      process.env.REACT_APP_TALENT_TALENTGETINTERVIEWREQUESTS,
      requestData,
      { 
        headers: { 
          Authorization: token, 
          "Content-Type": "application/json" 
        } 
      }
    );

    console.log('API Response:', response.data);

    if (response.data.status === 200) {
      const requests = response.data.data?.requests || [];
      const paginationInfo = response.data.data?.pagination || {};
      
      setInterviewRequests(requests);
      setPagination(paginationInfo);
      
      // If loading all requests, also store in allRequests for counting
      if (status === 'all') {
        setAllRequests(requests);
      }
      
      console.log('Loaded requests:', requests);
      console.log('Pagination:', paginationInfo);
    } else {
      console.error('API Error:', response.data);
      setSnackbar({
        open: true,
        message: response.data.message || response.data.data || 'Failed to load interview requests',
        severity: 'error'
      });
      setInterviewRequests([]);
    }
  } catch (error) {
    console.error('Error loading requests:', error);
    setSnackbar({
      open: true,
      message: error.response?.data?.message || 'Failed to load interview requests. Please try again.',
      severity: 'error'
    });
    setInterviewRequests([]);
  } finally {
    setLoading(false);
  }
};
// Function to load all requests for counting purposes
const loadAllRequestsForCounting = async () => {
  try {
    const requestData = {
      candidate_id: candidateId,
      client_code: clientCode,
      page: 1,
      limit: 100
      // No status filter to get all requests
    };

    const response = await axios.post(
     process.env.REACT_APP_TALENT_TALENTGETINTERVIEWREQUESTS,
      requestData,
      { 
        headers: { 
          Authorization: token, 
          "Content-Type": "application/json" 
        } 
      }
    );

    if (response.data.status === 200) {
      const allRequestsData = response.data.data?.requests || [];
      setAllRequests(allRequestsData);
      
      // If we're on "All" tab, also set the display requests
      if (activeTab === 3) {
        setInterviewRequests(allRequestsData);
        setPagination(response.data.data?.pagination || {});
      }
      
      console.log('Loaded all requests for counting:', allRequestsData.length);
    }
  } catch (error) {
    console.error('Error loading all requests for counting:', error);
  }
};
  const loadStatistics = async () => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_TALENTGETINTERVIEWSTATISSTICS,
        {
          env: "production",
          candidate_id: candidateId,
          client_code: clientCode
        },
        { 
          headers: { 
            Authorization: token, 
            "Content-Type": "application/json" 
          } 
        }
      );

      console.log('Statistics Response:', response.data);

      if (response.data.status === 200) {
        setStatistics(response.data.data || {});
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const getRequestDetails = async (requestId) => {
    setLoadingDetails(true); // Start loading
    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_TALENTGETINTERVIEWREQUESTDETIALS,
        {
          env: "production",
          request_id: requestId,
          candidate_id: candidateId,
          client_code: clientCode
        },
        { 
          headers: { 
            Authorization: token, 
            "Content-Type": "application/json" 
          } 
        }
      );

      console.log('Request Details Response:', response.data);

      if (response.data.status === 200) {
        setSelectedRequest(response.data.data);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || response.data.data || 'Failed to load request details',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error loading request details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to load request details. Please try again.',
        severity: 'error'
      });
    }
    finally {
    setLoadingDetails(false); // Stop loading
  }
  };

  const submitResponse = async () => {
    if (responseData.status === 'accepted' && 
        (responseData.availableDates.length === 0 || responseData.availableTimeSlots.length === 0)) {
      setSnackbar({
        open: true,
        message: 'Please select at least one date and time slot when accepting an interview',
        severity: 'error'
      });
      return;
    }

    setSubmitting(true);
    try {
      const requestPayload = {
        env: "production",
        request_id: selectedRequest.id,
        candidate_id: candidateId,
        client_code: clientCode,
        response_status: responseData.status,
        response_message: responseData.message || null
      };

      // Add availability data only for accepted requests
      if (responseData.status === 'accepted') {
        requestPayload.available_dates = responseData.availableDates;
        requestPayload.available_time_slots = responseData.availableTimeSlots;
      }

      console.log('Submitting response:', requestPayload);

      const response = await axios.post(
     process.env.REACT_APP_TALENT_TALENtRESPODTOINTERVIEWREQUEST,
        requestPayload,
        { 
          headers: { 
            Authorization: token, 
            "Content-Type": "application/json" 
          } 
        }
      );

      console.log('Submit Response:', response.data);

      if (response.data.status === 200) {
        // Reset states
        setShowResponseModal(false);
        setSelectedRequest(null);
        setResponseData({
          availableDates: [],
          availableTimeSlots: [],
          message: '',
          status: ''
        });

        setSnackbar({
          open: true,
          message: `Interview request ${responseData.status} successfully!`,
          severity: 'success'
        });

        // Reload the requests and statistics
        loadInterviewRequests(tabConfig[activeTab]);
        loadStatistics();
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || response.data.data || 'Failed to submit response',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting response:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to submit response. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Load data on component mount and when filters change
 useEffect(() => {
  if (candidateId && clientCode && token) {
    // Always load all requests first to get proper counts
    loadAllRequestsForCounting().then(() => {
      // Then load filtered requests for current tab
      if (activeTab !== 3) { // If not "All" tab
        loadInterviewRequests(tabConfig[activeTab]);
      }
    });
  } else {
    console.error('Missing required authentication data:', { candidateId, clientCode, token: !!token });
    setSnackbar({
      open: true,
      message: 'Authentication data missing. Please log in again.',
      severity: 'error'
    });
    setLoading(false);
  }
}, [activeTab, candidateId, clientCode, token]);
  useEffect(() => {
    if (candidateId && clientCode && token) {
      loadStatistics();
    }
  }, [candidateId, clientCode, token]);

  // Get counts for different statuses
  const getStatusCounts = () => {
  const counts = {
    pending: 0,
    accepted: 0,
    declined: 0,
    all: allRequests.length // Use allRequests for total count
  };

  // Count from allRequests instead of interviewRequests
  allRequests.forEach(req => {
    if (req.status && counts.hasOwnProperty(req.status)) {
      counts[req.status]++;
    }
  });

  return counts;
};

  // Filter requests based on active tab and search
  const filteredRequests = interviewRequests.filter(request => {
    const statusFilter = activeTab === 3 ? true : request.status === tabConfig[activeTab];
    const searchFilter = searchTerm === '' || 
      request.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.managerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase());
    return statusFilter && searchFilter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleRequestClick = (request) => {
    getRequestDetails(request.id);
  };

  const handleRespond = (status) => {
    setResponseData(prev => ({ ...prev, status }));
    setShowResponseModal(true);
  };

  const handleDateSelection = (date) => {
    setResponseData(prev => ({
      ...prev,
      availableDates: prev.availableDates.includes(date)
        ? prev.availableDates.filter(d => d !== date)
        : [...prev.availableDates, date]
    }));
  };

  const handleTimeSlotSelection = (slot) => {
    setResponseData(prev => ({
      ...prev,
      availableTimeSlots: prev.availableTimeSlots.includes(slot)
        ? prev.availableTimeSlots.filter(s => s !== slot)
        : [...prev.availableTimeSlots, slot]
    }));
  };

  const getNextSevenDays = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i <= 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const statusCounts = getStatusCounts();

  // Show loading backdrop only on initial load
  if (loading && interviewRequests.length === 0) {
    return (
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', maxWidth: '100%', marginLeft: collapsed ? "60px" : "240px" }}>
        <Backdrop open={true} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
              Loading interview requests...
            </Typography>
          </Box>
        </Backdrop>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh', maxWidth: '100%', marginLeft: collapsed ? "60px" : "240px" }}>
        {/* ADD THIS LOADER BACKDROP */}
    {loadingDetails && (
      <Backdrop 
        open={true} 
        sx={{ 
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: 'rgba(0, 0, 0, 0.5)' 
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <CircularProgress size={60} color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading request details...
          </Typography>
        </Box>
      </Backdrop>
    )}
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Interview Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Manage your interview opportunities
          </Typography>
          <IconButton color="inherit">
            <Badge badgeContent={statusCounts.pending} color="error">
              <Notifications />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {selectedRequest ? (
          /* Detailed Request View */
          <Card elevation={2}>
            <CardContent sx={{ p: 3 }}>
              {/* Back Button */}
             <Button
  startIcon={<ChevronRight sx={{ transform: 'rotate(180deg)' }} />}
  onClick={() => {
    setSelectedRequest(null);
    setLoadingDetails(false); // Reset loading state
  }}
  sx={{ mb: 3 }}
>
  Back to requests
</Button>

              {/* Request Header */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item>
                    <Avatar
                      src={selectedRequest.companyLogo}
                      alt={selectedRequest.companyName}
                      sx={{ width: 64, height: 64 }}
                    >
                      <Business />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h4" gutterBottom fontWeight={600}>
                      {selectedRequest.projectTitle || 'Interview Request'}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {selectedRequest.companyName || 'Company'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRequest.managerName || 'Manager'} • {selectedRequest.managerTitle || 'Position'}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Stack direction="row" spacing={1}>
                      <UrgencyChip
                        urgency={selectedRequest.urgency || 'medium'}
                        label={`${selectedRequest.urgency || 'Medium'} Priority`}
                        size="small"
                      />
                      <StatusChip
                        status={selectedRequest.status || 'pending'}
                        label={selectedRequest.status || 'pending'}
                        size="small"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Request Details */}
              <Grid container spacing={4}>
                <Grid item xs={12} lg={6}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Project Details
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachMoney fontSize="small" color="action" />
                          <Typography variant="body2">Compensation:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {selectedRequest.compensation || 'TBD'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <VideoCall fontSize="small" color="action" />
                          <Typography variant="body2">Interview Type:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedRequest.interviewType || 'Virtual'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2">Duration:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedRequest.estimatedDuration || '1 hour'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">Request Date:</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(selectedRequest.createdAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {selectedRequest.message && (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom fontWeight={600}>
                        Manager's Message
                      </Typography>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {selectedRequest.message}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Requested Interview Slots
                  </Typography>
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Preferred Dates:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedRequest.requestedDates?.length > 0 ? (
                        selectedRequest.requestedDates.map((date, index) => (
                          <Chip
                            key={index}
                            label={formatDate(date)}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No specific dates requested
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Preferred Times:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selectedRequest.requestedTimeSlots?.length > 0 ? (
                        selectedRequest.requestedTimeSlots.map((slot, index) => (
                          <Chip
                            key={index}
                            label={slot}
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No specific times requested
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {selectedRequest.status === 'accepted' && selectedRequest.responseMessage && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Your Response
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{selectedRequest.responseMessage}"
                      </Typography>
                      {selectedRequest.respondedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Responded on: {new Date(selectedRequest.respondedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {selectedRequest.status === 'declined' && selectedRequest.responseMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Your Response
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{selectedRequest.responseMessage}"
                      </Typography>
                      {selectedRequest.respondedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Responded on: {new Date(selectedRequest.respondedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Grid>
              </Grid>

              {/* Action Buttons */}
              {selectedRequest.status === 'pending' && (
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 4 }}>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Check />}
                      onClick={() => handleRespond('accepted')}
                      size="large"
                    >
                      Accept Interview
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Clear />}
                      onClick={() => handleRespond('declined')}
                      size="large"
                    >
                      Decline Interview
                    </Button>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Requests List View */
          <Box>
            {/* Filters and Search */}
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.pending} color="error">
                          Pending
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.accepted} color="success">
                          Accepted
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.declined} color="error" variant="dot">
                          Declined
                        </Badge>
                      }
                    />
                    <Tab
                      label={
                        <Badge badgeContent={statusCounts.all} color="primary">
                          All
                        </Badge>
                      }
                    />
                  </Tabs>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by company, manager, or project..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
              </Grid>

              {/* Show pagination info if available */}
              {pagination && pagination.total > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {interviewRequests.length} of {pagination.total} requests
                  </Typography>
                  {pagination.totalPages > 1 && (
                    <Typography variant="body2" color="text.secondary">
                      Page {pagination.page} of {pagination.totalPages}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>

            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Requests List */}
            {!loading && filteredRequests.length === 0 ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
                <Message sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Interview Requests
                </Typography>
                <Typography color="text.secondary">
                  {activeTab === 0 
                    ? "You don't have any pending interview requests."
                    : searchTerm 
                      ? "No requests match your search criteria."
                      : `You don't have any ${tabConfig[activeTab]} interview requests.`
                  }
                </Typography>
                {!candidateId || !clientCode || !token && (
                  <Alert severity="warning" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                    Authentication data is missing. Please check your login status.
                  </Alert>
                )}
              </Paper>
            ) : !loading && (
              <Stack spacing={2}>
                {filteredRequests.map((request) => (
                  <StyledCard
                    key={request.id}
                    onClick={() => handleRequestClick(request)}
                    elevation={1}
                  >
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <Avatar
                            src={request.companyLogo}
                            alt={request.companyName}
                            sx={{ width: 48, height: 48 }}
                          >
                            <Business />
                          </Avatar>
                        </Grid>
                        <Grid item xs>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {request.projectTitle || 'Interview Request'}
                              </Typography>
                              <Typography color="text.secondary" gutterBottom>
                                {request.companyName || 'Company Name'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {request.managerName || 'Manager'} • {request.managerTitle || 'Position'}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <UrgencyChip
                                urgency={request.urgency || 'medium'}
                                label={request.urgency || 'medium'}
                                size="small"
                              />
                              <StatusChip
                                status={request.status || 'pending'}
                                label={request.status || 'pending'}
                                size="small"
                              />
                            </Stack>
                          </Box>
                          
                          <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CalendarToday fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {request.requestedDates?.length || 0} date{(request.requestedDates?.length || 0) > 1 ? 's' : ''} available
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {request.estimatedDuration || '1 hour'}
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              {request.compensation || 'TBD'}
                            </Typography>
                          </Box>

                          {request.message && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {request.message.substring(0, 150)}
                              {request.message.length > 150 && '...'}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item>
                          <ChevronRight color="action" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </StyledCard>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* Response Modal */}
        <Dialog
          open={showResponseModal}
          onClose={() => !submitting && setShowResponseModal(false)}
          maxWidth="md"
          fullWidth
        >
            {/* ADD THIS BACKDROP LOADER */}
  {submitting && (
    <Backdrop 
      open={true} 
      sx={{ 
        position: 'absolute',
        zIndex: (theme) => theme.zIndex.modal + 1,
        bgcolor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 1
      }}
    >
      <Box sx={{ textAlign: 'center', color: 'primary.main' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {responseData.status === 'accepted' ? 'Accepting interview...' : 'Declining interview...'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we process your response
        </Typography>
      </Box>
    </Backdrop>
  )}
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {responseData.status === 'accepted' ? 'Accept' : 'Decline'} Interview Request
              </Typography>
              {!submitting && (
                <IconButton onClick={() => setShowResponseModal(false)}>
                  <Close />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {responseData.status === 'accepted' && (
              <>
                {/* Available Dates */}
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1 }} />
                    Select Your Available Dates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose dates that work for you from the next 7 days:
                  </Typography>
                  <Grid container spacing={1}>
                    {getNextSevenDays().map((date) => (
                      <Grid item xs={6} sm={4} key={date}>
                        <ToggleButton
                          fullWidth
                          value={date}
                          selected={responseData.availableDates.includes(date)}
                          onChange={() => handleDateSelection(date)}
                          disabled={submitting}
                          sx={{ flexDirection: 'column', p: 1 }}
                        >
                          <Typography variant="body2">
                            {formatDate(date)}
                          </Typography>
                          {selectedRequest?.requestedDates?.includes(date) && (
                            <Typography variant="caption" color="primary">
                              Requested
                            </Typography>
                          )}
                        </ToggleButton>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Available Time Slots */}
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                    <AccessTime sx={{ mr: 1 }} />
                    Select Your Available Time Slots
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose time slots that work for you:
                  </Typography>
                  <Grid container spacing={1}>
                    {timeSlots.map((slot) => (
                      <Grid item xs={6} sm={4} key={slot}>
                        <ToggleButton
                          fullWidth
                          value={slot}
                          selected={responseData.availableTimeSlots.includes(slot)}
                          onChange={() => handleTimeSlotSelection(slot)}
                          disabled={submitting}
                          sx={{ flexDirection: 'column', p: 1 }}
                        >
                          <Typography variant="body2">
                            {slot}
                          </Typography>
                          {selectedRequest?.requestedTimeSlots?.includes(slot) && (
                            <Typography variant="caption" color="primary">
                              Requested
                            </Typography>
                          )}
                        </ToggleButton>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}

            {/* Message */}
            <Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  responseData.status === 'accepted' 
                    ? 'Message to Manager (Optional)' 
                    : 'Reason for Declining (Optional)'
                }
                placeholder={
                  responseData.status === 'accepted'
                    ? "Share your enthusiasm about the opportunity..."
                    : "Provide a polite reason for declining..."
                }
                value={responseData.message}
                onChange={(e) => setResponseData(prev => ({ ...prev, message: e.target.value }))}
                disabled={submitting}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowResponseModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={responseData.status === 'accepted' ? 'success' : 'error'}
              onClick={submitResponse}
              disabled={
                submitting ||
                (responseData.status === 'accepted' && 
                (responseData.availableDates.length === 0 || responseData.availableTimeSlots.length === 0))
              }
              startIcon={submitting ? <CircularProgress size={16} /> : (responseData.status === 'accepted' ? <Check /> : <Clear />)}
            >
              {submitting ? 'Submitting...' : (responseData.status === 'accepted' ? 'Accept Interview' : 'Decline Interview')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TalentInterviewDashboard;